import type { ReactNode } from "react";

/**
 * ============================================================================
 *  ambianceIcones.tsx — Les pictogrammes du contrôleur d'ambiances
 * ----------------------------------------------------------------------------
 *  Repris du handoff (`sources/design/design_handoff_controleur_ambiances/`) :
 *  SVG 24 × 24, trait 1,5, couleur héritée du bouton (`currentColor`). Quelle
 *  valeur d'axe prend quel pictogramme se décide dans `ambiances.json`, pas
 *  ici : une valeur sans pictogramme déclaré prend le losange générique.
 * ==========================================================================*/

export type IconeAmbiance =
  | "sun"
  | "sunset"
  | "dusk"
  | "moon"
  | "leaf"
  | "snow"
  | "flower"
  | "bulbOff"
  | "bulbOn"
  | "dot";

const AMPOULE = "M9 18h6M10 21h4M8.5 14.5A6 6 0 1 1 15.5 14.5c-.6.6-1 1.3-1 2.2V18h-5v-1.3c0-.9-.4-1.6-1-2.2Z";

const TRACES: Record<IconeAmbiance, ReactNode> = {
  sun: (
    <>
      <circle cx={12} cy={12} r={4} />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4" />
    </>
  ),
  sunset: <path d="M7 16a5 5 0 0 1 10 0M3 16h18M6 20h12M12 6V8.5M5.6 9.6l1.4 1.4M18.4 9.6 17 11" />,
  dusk: (
    <>
      <path d="M3 17h18M7 21h10M8 17a4 4 0 0 1 8 0" />
      <path d="M17 4.5v2M16 5.5h2M7 7v1.6M6.2 7.8h1.6" />
    </>
  ),
  moon: <path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5Z" />,
  leaf: (
    <>
      <path d="M5 19C5 10 10 5 19 5c0 9-5 14-14 14Z" />
      <path d="M5 19 13 11" />
    </>
  ),
  snow: <path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9M9.5 4.5 12 6.5l2.5-2M9.5 19.5 12 17.5l2.5 2" />,
  flower: (
    <>
      <circle cx={12} cy={10} r={2} />
      <path d="M12 8c0-3 3-4 3-1.5S12 10 12 8ZM12 12v9M12 17c-3 0-4.5-2-4.5-3.5 2 0 4.5 1 4.5 3.5Z" />
      <path d="M10 10c-3 0-4-3-1.5-3S12 10 10 10ZM14 10c3 0 4-3 1.5-3S12 10 14 10Z" />
    </>
  ),
  bulbOff: <path d={AMPOULE} />,
  bulbOn: (
    <>
      <path d={AMPOULE} />
      <path d="M12 1v1.2M3.5 4.5l.9.9M20.5 4.5l-.9.9M1.5 11h1.2M21.3 11h1.2" />
    </>
  ),
  dot: <path d="M12 8.5 15.5 12 12 15.5 8.5 12Z" />,
};

const estIcone = (nom: string): nom is IconeAmbiance => nom in TRACES;

/** Le pictogramme d'un nom déclaré dans `ambiances.json`, ou le losange générique. */
export function IconeAmbianceSvg({ nom, taille }: { nom: string | undefined; taille: number }) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "none" }}
    >
      {TRACES[nom !== undefined && estIcone(nom) ? nom : "dot"]}
    </svg>
  );
}
